import { useCallback, useMemo } from "react";

import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import type { PatientSMTPermissions } from "@/store/api/smt";
import {
  useGetPatientSMTPermissionsQuery,
  useGetSupportSMTPermissionsQuery,
} from "@/store/api/smt";
import {
  useAppUserProxyPatientsInTreatmentSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import { canAddMeals, isViewable } from "@/utils/smt.util";

type LinkedPatientProfileWithMaybeSMT = {
  smtPermissionType?: SMTPermissionType;
} & LinkedPatientProfile;

type LinkedPatientProfileWithSMT = {
  smtPermissionType: SMTPermissionType;
} & LinkedPatientProfile;

interface AppUserSMTPermissions {
  patientSMTPermissions?: PatientSMTPermissions;
  refetchSMTPermissions: () => Promise<void>;
  shouldAppUserSeeAddSMTEntriesButton: boolean;
  shouldAppUserSeeWeekLogSection?: boolean;
  supportPatientsEligibleForSMT: {
    supportPatientsWithSMTAddAccess: LinkedPatientProfileWithSMT[];
    supportPatientsWithSMTViewAccess: LinkedPatientProfileWithSMT[];
  };
}

const canAddPatientSMT = (permissions?: PatientSMTPermissions): boolean => {
  if (!permissions) return false;
  return permissions.isSMTEnabled && canAddMeals(permissions.permissionType);
};

const canViewPatientSMT = (permissions?: PatientSMTPermissions): boolean => {
  if (!permissions) return false;
  return permissions.isSMTEnabled && isViewable(permissions.permissionType);
};

const useAppUserSMTPermissions = (): AppUserSMTPermissions => {
  const { externalId: appUserId, isPatient: isAppUserPatient } =
    useAppUserSelector();
  const inTreatmentProxyPatients = useAppUserProxyPatientsInTreatmentSelector();

  const { data: patientSMTPermissions, refetch: refetchPatientSMTPermissions } =
    useGetPatientSMTPermissionsQuery(
      {
        patientId: appUserId,
      },
      {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        skip: isEmpty(appUserId) || !isAppUserPatient,
      },
    );
  const { data: supportSMTPermissions, refetch: refetchSupportSMTPermissions } =
    useGetSupportSMTPermissionsQuery(
      {
        supportId: appUserId,
      },
      {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        skip: isEmpty(appUserId) || isAppUserPatient,
      },
    );

  const refetchSMTPermissions = useCallback(async () => {
    if (isEmpty(appUserId)) return;
    if (isAppUserPatient) {
      await refetchPatientSMTPermissions();
    } else {
      await refetchSupportSMTPermissions();
    }
  }, [
    appUserId,
    isAppUserPatient,
    refetchPatientSMTPermissions,
    refetchSupportSMTPermissions,
  ]);

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- forced type assertion since tsc doesn't recognize the !isNil filtering
  const supportPatientsEligibleForSMT = useMemo(() => {
    if (isNil(supportSMTPermissions) || isNil(inTreatmentProxyPatients)) {
      return [];
    }
    // if support's isSMTEnabled is false, ignore everything else, and return an empty list
    if (!supportSMTPermissions.isSMTEnabled) {
      return [];
    }

    // get patient IDs who the support appUser has VIEW or ADD permission
    const { permissions } = supportSMTPermissions;
    const eligiblePatientIds = new Set(
      permissions
        .filter(
          // isViewable technically includes canAddMeals, but including both to be explicit
          (p) => isViewable(p.permissionType) || canAddMeals(p.permissionType),
        )
        .map((p) => p.patientId),
    );

    // get patient LinkedPatientProfiles that match those patient IDs
    const eligiblePatients = inTreatmentProxyPatients.filter((p) =>
      eligiblePatientIds.has(p.externalId),
    );

    // augment patient LinkedPatientProfiles with their SMTPermissionType
    const eligiblePatientsWithSMT: LinkedPatientProfileWithMaybeSMT[] =
      eligiblePatients.map((eligiblePatient) => {
        const smtPermissionType = permissions.find((permission) => {
          return permission.patientId === eligiblePatient.externalId;
        })?.permissionType;
        return {
          ...eligiblePatient,
          smtPermissionType,
        };
      });

    return eligiblePatientsWithSMT.filter((p) => !isNil(p.smtPermissionType));
  }, [
    inTreatmentProxyPatients,
    supportSMTPermissions,
  ]) as LinkedPatientProfileWithSMT[];

  const supportPatientsWithSMTViewAccess = useMemo(() => {
    return supportPatientsEligibleForSMT.filter((eligiblePatient) =>
      isViewable(eligiblePatient.smtPermissionType),
    );
  }, [supportPatientsEligibleForSMT]);

  const supportPatientsWithSMTAddAccess = useMemo(() => {
    return supportPatientsEligibleForSMT.filter((eligiblePatient) =>
      canAddMeals(eligiblePatient.smtPermissionType),
    );
  }, [supportPatientsEligibleForSMT]);

  const shouldAppUserSeeAddSMTEntriesButton = isAppUserPatient
    ? canAddPatientSMT(patientSMTPermissions)
    : !isEmpty(supportPatientsWithSMTAddAccess);

  const shouldAppUserSeeWeekLogSection = isAppUserPatient
    ? canViewPatientSMT(patientSMTPermissions)
    : !isEmpty(supportPatientsWithSMTViewAccess);

  return {
    patientSMTPermissions,
    refetchSMTPermissions,
    shouldAppUserSeeAddSMTEntriesButton,
    shouldAppUserSeeWeekLogSection,
    supportPatientsEligibleForSMT: {
      supportPatientsWithSMTAddAccess,
      supportPatientsWithSMTViewAccess,
    },
  };
};

export default useAppUserSMTPermissions;
export type { AppUserSMTPermissions, LinkedPatientProfileWithSMT };
